import {
  Box,
  Button,
  Checkbox,
  Divider,
  HStack,
  Stack,
  Text,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import { useEffectOnce } from "usehooks-ts";
import { FaRegCirclePlay } from "react-icons/fa6";
import { H2 } from "@/components/Heading";
import { FormInput } from "@/components/Form/FormInput";
import { useStore } from "@/store";
import { createTrainingData } from "@/store/types/training";
import { AppLink } from "@/routing/AppLink";
import { translations } from "@/constants/translations";
import { renderInnerHtml } from "@/helpers/dom";
import { telemetry } from "@/telemetry";

const getWatchSeconds = (video: HTMLVideoElement | null) => {
  if (!video) return 0;

  let seconds = 0;

  for (let index = 0; index < video.played.length; index++)
    seconds += video.played.end(index) - video.played.start(index);

  return Math.round(seconds);
};

export const TrainingPage = observer(() => {
  const store = useStore();
  const { trainingData, activeLanguage } = store;
  const [hasWatched, setWatched] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasStartedVideo = useRef(false);
  const hasReportedVideoFailure = useRef(false);
  const language = translations.pages["training"][activeLanguage];
  const formLanguage = translations.common.form[activeLanguage];

  useEffectOnce(() => {
    store.trainingData = createTrainingData();
  });

  const handleNameChange = (value: string) => {
    trainingData.name = value;
  };

  const handleConfirmCheck = () => {
    trainingData.isConfirmReviewed = !trainingData.isConfirmReviewed;
  };

  const toggleVideoState = () => {
    if (!isPlaying && !hasStartedVideo.current) {
      hasStartedVideo.current = true;
      telemetry.trackEvent("training_video_started");
    }

    if (!isPlaying) videoRef.current?.play();
    else videoRef.current?.pause();
    setPlaying(!isPlaying);
  };

  const handleVideoEnd = () => {
    setWatched(true);
    telemetry.trackEvent("training_video_completed", {
      watch_seconds: getWatchSeconds(videoRef.current),
    });
  };

  const handleVideoError = () => {
    if (hasReportedVideoFailure.current) return;

    hasReportedVideoFailure.current = true;
    telemetry.trackEvent("video_load_failed", {
      error_code: videoRef.current?.error?.code,
      network_state: videoRef.current?.networkState,
    });
  };

  const canProceed = hasWatched && trainingData.isConfirmReviewed;

  const handleGenerate = () => {
    if (canProceed) telemetry.trackEvent("certificate_generated");
  };

  return (
    <Stack spacing="14">
      <H2 textAlign="center">{renderInnerHtml(language.heading)}</H2>
      <Stack spacing="8">
        <Box position="relative">
          <Box
            position="absolute"
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex="1"
            onClick={toggleVideoState}
            cursor="pointer"
          >
            {!isPlaying && (
              <FaRegCirclePlay
                color="black"
                size={72}
              />
            )}
          </Box>
          <video
            ref={videoRef}
            onEnded={handleVideoEnd}
            onError={handleVideoError}
            playsInline
          >
            <source
              src={language.video_url}
              type="video/mp4"
              onError={handleVideoError}
            />
          </video>
        </Box>
        <Text fontSize="16px">
          {renderInnerHtml(language.video_description)}
        </Text>
      </Stack>
      <Divider />

      <Stack spacing="8">
        <H2>{language.video_acknowledge}</H2>
        <FormInput
          maxW="container.sm"
          label={formLanguage.name}
          onChange={(e) => handleNameChange(e.target.value)}
          value={trainingData.name}
        />
        <Checkbox
          isChecked={trainingData.isConfirmReviewed}
          onChange={handleConfirmCheck}
        >
          {renderInnerHtml(language.confirm_label)}
        </Checkbox>
      </Stack>

      <HStack justifyContent="flex-end">
        <AppLink
          to="/training/certificate"
          onClick={handleGenerate}
        >
          <Button isDisabled={!canProceed}>{language.generate_button}</Button>
        </AppLink>
      </HStack>
    </Stack>
  );
});
